import React, { useEffect, useState } from 'react';
import { Button, Alert, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { useProfile } from '../../context/userContext';
import { useAuth0 } from '@auth0/auth0-react';
import { useAuth } from '../../context/authContext';
import { getProductsAndPrices, createIndividualCheckout } from '../../network/apis/subscription.api';
import styles from '../../styles/IndividualPlans.module.css';

const IndividualPlans = ({ onSwitchToOrg }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { profile } = useProfile();
  const { loginWithRedirect } = useAuth0();
  const { authenticated, token } = useAuth();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const data = await getProductsAndPrices();
        setPlans(data);
        setLoading(false);
      } catch (error) {
        setError('Unable to load plans. Please try again later.');
        setLoading(false);
      }
    };

    fetchPlans();
  }, []);

  const handleCheckout = async (priceIds) => {
    if (!authenticated) {
      loginWithRedirect();
      return;
    }

    try {
      const { url } = await createIndividualCheckout(token, {
        priceIds,
        customerId: profile.userId,
        email: profile.email,
      });
      window.location.href = url;
    } catch (error) {
      setError('An error occurred while processing your checkout. Please try again.');
    }
  };

  const formatPrice = (amount) => {
    if (!amount || amount === 0) return 'Free';
    return `$${(amount / 100).toFixed(2)}`;
  };

  const renderFeature = (feature, index) => {
    const isIncluded = !feature.startsWith('×');
    const featureText = feature.replace(/^[✓×]\s*/, '');

    return (
      <li key={index} className={styles['feature-item']}>
        {isIncluded ? (
          <CheckCircleIcon
            className={styles['icon-check']}
            aria-hidden="true"
            fontSize="small"
          />
        ) : (
          <CancelIcon
            className={styles['icon-cancel']}
            aria-hidden="true"
            fontSize="small"
          />
        )}
        <span className={isIncluded ? styles['feature-included'] : styles['feature-excluded']}>
          {featureText}
        </span>
      </li>
    );
  };

  const renderPlanCard = (plan, isHighlighted = false) => {
    const monthlyPrice = plan.prices.find(p => p.recurring?.interval === 'month');
    const isCurrentPlan = profile?.subscription?.stripeProductId === plan.id;
    const isActive = plan.active === 'true';

    // Check if user has a subscription and this is a different plan (upgrade/switch)
    const hasActiveSubscription = profile?.subscription?.stripeProductId && !isCurrentPlan;

    // Check if this is a cross-category upgrade (user has subscription but plan is different category)
    const isCrossCategoryUpgrade = hasActiveSubscription && profile?.faaIDType && (
      (profile.faaIDType === 'Part 107' && plan.name.includes('44809')) ||
      (profile.faaIDType === '44809' && plan.name.includes('Part 107'))
    );

    return (
      <article
        key={plan.id}
        className={`${styles['plan-card']} ${isHighlighted ? styles['plan-highlighted'] : ''} ${!isActive ? styles['plan-inactive'] : ''}`}
        aria-label={`${plan.name} plan`}
      >
        {isHighlighted && (
          <div className={styles['popular-badge']} aria-label="Most popular plan">
            Most Popular
          </div>
        )}

        <header className={styles['plan-header']}>
          <h3 className={styles['plan-name']}>{plan.name}</h3>
          <div className={styles['plan-price']}>
            <span className={styles['price-amount']} aria-label={`Price: ${formatPrice(monthlyPrice?.unit_amount)} per month`}>
              {formatPrice(monthlyPrice?.unit_amount)}
            </span>
            {monthlyPrice?.unit_amount > 0 && (
              <span className={styles['price-period']} aria-hidden="true">/month</span>
            )}
          </div>
          <p className={styles['plan-description']}>{plan.description}</p>
        </header>

        <div className={styles['plan-body']}>
          <h4 className={styles['features-heading']}>Features included:</h4>
          <ul className={styles['features-list']} aria-label={`Features for ${plan.name} plan`}>
            {plan.features.map(renderFeature)}
          </ul>
        </div>

        <footer className={styles['plan-footer']}>
          {!isActive ? (
            <p className={styles['notice']} role="status">
              {plan.notice || 'Coming Soon'}
            </p>
          ) : isCurrentPlan ? (
            <Button
              variant="outlined"
              fullWidth
              disabled
              aria-label="Currently on this plan"
              className={styles['btn-current']}
            >
              Current Plan
            </Button>
          ) : (
            <Button
              variant={isHighlighted ? 'contained' : 'outlined'}
              color="primary"
              fullWidth
              onClick={() => {
                const sortedPriceIds = plan.prices
                  .sort((a, b) => a.recurring?.interval === 'month' ? -1 : 1)
                  .map(price => price.id);
                handleCheckout(sortedPriceIds);
              }}
              aria-label={hasActiveSubscription ? `Switch to ${plan.name}` : `Get started with ${plan.name} plan`}
              className={styles['btn-get-started']}
            >
              {monthlyPrice?.unit_amount === 0
                ? 'Get Started'
                : hasActiveSubscription
                  ? (isCrossCategoryUpgrade ? 'Switch Plan' : 'Upgrade')
                  : 'Start 7-Day Trial'}
            </Button>
          )}
        </footer>
      </article>
    );
  };

  if (loading) {
    return (
      <div className={styles['loading-container']} role="status" aria-live="polite">
        <CircularProgress aria-label="Loading plans" />
        <span className="sr-only">Loading individual plans...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error" role="alert" className={styles['error-alert']}>
        {error}
      </Alert>
    );
  }

  // Group plans by type
  const part107Plans = plans
    .filter(plan => plan.name.includes('Part 107'))
    .sort((a, b) => a.display_order - b.display_order);

  const plans44809 = plans
    .filter(plan => plan.name.includes('44809'))
    .sort((a, b) => a.display_order - b.display_order);

  return (
    <div className={styles['individual-plans-container']}>
      <div className={styles['plans-intro']}>
        <h2 className={styles['section-title']}>Individual Plans</h2>
        <p className={styles['section-description']}>
          Perfect for solo drone operators. All plans include LAANC authorizations and mission logs.
        </p>
      </div>

      {/* Show grouped plans for unauthenticated users or all plans */}
      {!authenticated || !profile?.faaIDType ? (
        <div className={styles['plans-wrapper']}>
          <div className={styles['plans-group-container']}>
            <h3 className={styles['group-heading']}>Commercial (Part 107)</h3>
            <div className={styles['plans-grid']} role="list">
              {part107Plans.map((plan) => renderPlanCard(plan, false))}
            </div>
          </div>

          <div className={styles['vertical-separator']}></div>

          <div className={styles['plans-group-container']}>
            <h3 className={styles['group-heading']}>Recreational (44809)</h3>
            <div className={styles['plans-grid']} role="list">
              {plans44809.map((plan) => renderPlanCard(plan, false))}
            </div>
          </div>
        </div>
      ) : (
        /* Show user's plan category AND cross-category upgrade options */
        <div>
          {/* User's current plan category */}
          <div className={styles['plans-group-container']}>
            <h3 className={styles['group-heading']}>
              {profile.faaIDType === 'Part 107' ? 'Commercial (Part 107)' : 'Recreational (44809)'}
            </h3>
            <div className={styles['plans-grid']} role="list">
              {profile.faaIDType === 'Part 107'
                ? part107Plans.map((plan) => renderPlanCard(plan, false))
                : plans44809.map((plan) => renderPlanCard(plan, false))
              }
            </div>
          </div>

          {/* Cross-category upgrade options - Don't show 44809 plans if user has Commercial (Part 107) plan */}
          {profile?.subscription && profile.faaIDType !== 'Part 107' && (
            <div className={styles['upgrade-section']}>
              <h3 className={styles['group-heading']}>
                Commercial (Part 107) - Upgrade Options
              </h3>
              <p className={styles['upgrade-description']}>
                Switch to commercial plans if your flight operations change.
              </p>
              <div className={styles['plans-grid']} role="list">
                {part107Plans.map((plan) => renderPlanCard(plan, false))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Upgrade CTA */}
      {authenticated && (
        <aside className={styles['upgrade-cta']} aria-labelledby="org-upgrade-heading">
          <h3 id="org-upgrade-heading" className={styles['cta-title']}>
            Need team collaboration?
          </h3>
          <p className={styles['cta-description']}>
            Organization plans start at $199/month with multi-user access,
            advanced fleet management, and role-based permissions.
          </p>
          <Button
            variant="outlined"
            color="primary"
            onClick={onSwitchToOrg}
            aria-label="View organization plans"
            className={styles['btn-switch']}
          >
            View Organization Plans
          </Button>
        </aside>
      )}

      {/* Feature Comparison Note */}
      <div className={styles['comparison-note']}>
        <p>
          <strong>Note:</strong> Fleet management limits are configurable per plan.
          Contact support for custom limits.
        </p>
      </div>
    </div>
  );
};

export default IndividualPlans;
